import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Brain, AlertCircle, ArrowRight, CheckCircle2, Zap, HeartPulse, Heart, RefreshCw, Moon, AlertTriangle } from 'lucide-react';
const symptoms = [{
  icon: Heart,
  text: 'Tight chest & racing heart — even when "nothing is wrong"'
}, {
  icon: RefreshCw,
  text: "Overthinking loops you can't stop — your mind won't shut off"
}, {
  icon: Moon,
  text: "Restless sleep — you're exhausted but your body won't rest"
}, {
  icon: Zap,
  text: 'Constant background tension — like waiting for something bad'
}, {
  icon: AlertTriangle,
  text: 'Panic about the panic — the spiral that makes everything worse'
}, {
  icon: Brain,
  text: '"Just relax" doesn\'t work — because this isn\'t a mindset problem'
}];
const reasons = [{
  icon: Brain,
  title: 'Thinking vs. Sensing',
  description: 'You cannot out-think a body that believes it is under threat. Logic goes offline.',
  descriptionJsx: true,
  bgColor: 'bg-indigo-50',
  borderColor: 'border-indigo-100',
  iconBg: 'bg-indigo-100 text-indigo-700'
}, {
  icon: Activity,
  title: 'The Loop',
  description: "Try to calm down → can't → panic about the panic → spiral. That's biology, not weakness.",
  descriptionJsx: true,
  bgColor: 'bg-violet-50',
  borderColor: 'border-violet-100',
  iconBg: 'bg-violet-100 text-violet-700'
}, {
  icon: Zap,
  title: 'Wrong Inputs',
  description: 'Your nervous system is receiving danger signals, so it keeps outputting "alert" state.',
  descriptionJsx: true,
  bgColor: 'bg-rose-50',
  borderColor: 'border-rose-100',
  iconBg: 'bg-rose-100 text-rose-700'
}, {
  icon: HeartPulse,
  title: 'The Solution',
  description: 'Anxiety lowers when your body receives consistent signals of safety. Daily. Structured.',
  descriptionJsx: true,
  bgColor: 'bg-blue-50',
  borderColor: 'border-blue-100',
  iconBg: 'bg-blue-100 text-blue-700'
}];
export function ProblemSection() {
  return <section className="py-20 bg-[#FAFAFF] relative overflow-hidden" data-id="element-257">
      <div className="absolute top-20 left-0 w-72 h-72 bg-[#E0E7FF]/30 rounded-full blur-3xl" data-id="element-258" />
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-[#EDE9FE]/30 rounded-full blur-3xl" data-id="element-259" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-260">
        <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-261">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-100 text-rose-700 text-sm font-semibold mb-6" data-id="element-262">
            <AlertCircle className="w-4 h-4" data-id="element-263" />
            The Real Problem
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold text-[#1E1B4B] mb-6 leading-tight" data-id="element-264">
            Anxiety isn't a{' '}
            <span className="relative inline-block" data-id="element-265">
              <span className="relative z-10" data-id="element-266">willpower</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-rose-200/70 -z-0 rounded-sm" data-id="element-267"></span>
            </span>{' '}
            problem.
            <br data-id="element-268" />
            <span className="text-[#4338CA]" data-id="element-269">
              It's a{' '}
              <span className="relative inline-block" data-id="element-270">
                <span className="relative z-10" data-id="element-271">signal</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-[#FDE68A]/60 -z-0 rounded-sm" data-id="element-272"></span>
              </span>{' '}
              problem.
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-[#44403C] leading-relaxed max-w-2xl mx-auto" data-id="element-273">
            Anxiety doesn't show up like a "negative thought." It shows up like
            a{' '}
            <span className="font-bold text-[#1E1B4B] bg-[#FDE68A]/40 px-1 rounded" data-id="element-274">
              body alarm that won't turn off.
            </span>
          </motion.p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto mb-16" data-id="element-275">
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E0E7FF] shadow-sm" data-id="element-276">
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-2 flex items-center gap-2" data-id="element-277">
              <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-278" /> Does this feel
              familiar?
            </h3>
            <p className="text-sm text-rose-600 mb-4" data-id="element-279">
              If you checked even ONE of these, your nervous system is sending
              distress signals.
            </p>
            <ul className="space-y-3" data-id="element-280">
              {symptoms.map((symptom, index) => <motion.li key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="flex items-start gap-3 text-[#44403C]" data-id="element-281">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mt-0.5" data-id="element-282">
                    <symptom.icon className="w-3.5 h-3.5" data-id="element-283" />
                  </span>
                  <span className="text-base" data-id="element-284">{symptom.text}</span>
                </motion.li>)}
            </ul>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="flex justify-center mb-12" data-id="element-285">
          <div className="w-16 h-16 rounded-full bg-[#EEF2FF] flex items-center justify-center border border-[#E0E7FF]" data-id="element-286">
            <ArrowRight className="w-8 h-8 text-[#4338CA] rotate-90" data-id="element-287" />
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-288">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EEF2FF] text-[#4338CA] text-sm font-semibold mb-4" data-id="element-289">
            <CheckCircle2 className="w-4 h-4" data-id="element-290" />
            The Mechanism
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-[#1E1B4B]" data-id="element-291">
            Change the signals →{' '}
            <span className="bg-[#FDE68A]/50 px-1 rounded" data-id="element-292">
              Change the state
            </span>
          </h3>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-293">
          {reasons.map((reason, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.15
        }} className={`${reason.bgColor} p-8 rounded-2xl border ${reason.borderColor} relative overflow-hidden hover:shadow-md transition-shadow`} data-id="element-294">
              <div className={`w-12 h-12 ${reason.iconBg} rounded-xl flex items-center justify-center mb-4`} data-id="element-295">
                <reason.icon className="w-6 h-6" data-id="element-296" />
              </div>
              <h3 className="text-xl font-bold text-[#1E1B4B] mb-2" data-id="element-297">
                {reason.title}
              </h3>
              <p className="text-[#44403C] leading-relaxed text-sm" data-id="element-298">
                {index === 0 && <>
                    You cannot out-think a body that believes it is under
                    threat.{' '}
                    <span className="font-semibold text-[#1E1B4B]" data-id="element-299">
                      Logic goes offline.
                    </span>
                  </>}
                {index === 1 && <>
                    Try to calm down → can't → panic about the panic → spiral.{' '}
                    <span className="font-semibold text-[#4338CA]" data-id="element-300">
                      That's biology, not weakness.
                    </span>
                  </>}
                {index === 2 && <>
                    Your nervous system is receiving{' '}
                    <span className="bg-rose-100 px-1 rounded font-medium" data-id="element-301">
                      danger signals
                    </span>
                    , so it keeps outputting "alert" state.
                  </>}
                {index === 3 && <>
                    Anxiety lowers when your body receives{' '}
                    <span className="bg-[#FDE68A]/50 px-1 rounded font-semibold text-[#1E1B4B]" data-id="element-302">
                      consistent signals of safety.
                    </span>{' '}
                    Daily. Structured.
                  </>}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}